import React, { useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
// NOTE: Older TypeScript (4.1) in this project struggles with the advanced generic definitions
// exported by material-react-table. To avoid compile-time errors, we only import the column def type
// and loosen the handler signatures. When TS is upgraded (>=4.7) these can be restored.
import MaterialReactTable, { MRT_ColumnDef } from 'material-react-table';

// Minimal handler signatures (functional updater union avoided for TS 4.1 compatibility)
type PaginationChangeHandler = (updater: any) => void;
type SortingChangeHandler = (updater: any) => void;
type ColumnFiltersChangeHandler = (updater: any) => void;

export interface MRTTableProps<T extends Record<string, any>> {
  columns: MRT_ColumnDef<T>[];
  data: T[];
  loading?: boolean;
  manualPagination?: boolean;
  manualSorting?: boolean;
  totalRowCount?: number;
  pageIndex?: number;
  pageSize?: number;
  onPaginationChange?: PaginationChangeHandler;
  onSortingChange?: SortingChangeHandler;
  sorting?: any[];
  columnFilters?: any[];
  onColumnFiltersChange?: ColumnFiltersChangeHandler;
  // Global filter control (optional)
  globalFilter?: string;
  onGlobalFilterChange?: (val: any) => void;
  enableFilterMatchHighlighting?: boolean;
  tableContainerSx?: Record<string, any>;
  // Row selection
  enableRowSelection?: boolean;
  rowSelection?: any;
  onRowSelectionChange?: (updater: any) => void;
  getRowId?: (row: T, index?: number) => string;
  // Disable sorting globally
  enableSorting?: boolean;
  // Optional custom content on the left side of the top toolbar
  leftTopToolbar?: React.ReactNode;
}

/**
 * Thin wrapper around MaterialReactTable so we have one place to tune defaults.
 */
class MRTErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error?: any }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error: any) { return { hasError: true, error }; }
  componentDidCatch(error: any, info: any) { /* could log */ }
  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 2, border: '1px solid #f44336', borderRadius: 1, color: '#f44336' }}>
          MRT failed to render (experimental). {String(this.state.error?.message || '')}
        </Box>
      );
    }
    return this.props.children as any;
  }
}

export function MRTTable<T extends Record<string, any>>({
  columns,
  data,
  loading,
  manualPagination,
  manualSorting,
  totalRowCount,
  pageIndex,
  pageSize,
  onPaginationChange,
  onSortingChange,
  sorting,
  columnFilters,
  onColumnFiltersChange,
  globalFilter,
  onGlobalFilterChange,
  enableFilterMatchHighlighting,
  tableContainerSx,
  enableRowSelection,
  rowSelection,
  onRowSelectionChange,
  getRowId,
  enableSorting,
  leftTopToolbar,
}: MRTTableProps<T>) {
  const memoCols = useMemo(() => columns, [columns]);
  // Show skeleton rows only when loading AND there are no rows yet (prevents thin empty rows after load)
  const showSkeletons = !!loading && (!Array.isArray(data) || data.length === 0);
  // Show spinner immediately when loading starts, regardless of data state (like legacy TanStack table)
  const showSpinner = !!loading;
  // Control table data to prevent empty state during loading
  const tableData = loading ? (data || []) : data;

  // Exact-match, case-insensitive global filter (only for client-side filtering)
  const exactGlobalFilter = (row: any, columnId: string, filterValue: any) => {
    if (!row) return true;
    const val = typeof row.getValue === 'function'
      ? row.getValue(columnId)
      : (row.original ? row.original[columnId] : (row[columnId]));
    const a = (val ?? '').toString().trim().toLowerCase();
    const b = (filterValue ?? '').toString().trim().toLowerCase();
    if (!b) return true; // no filter -> include
    return a === b;
  };

  // Build props incrementally to avoid passing partially controlled state that can break older MRT versions
  const tableProps: any = {
    columns: memoCols,
    data: tableData,
  enableColumnFilters: false,
  enableGlobalFilter: true,
  positionGlobalFilter: 'right',
  enableDensityToggle: false,
  enableHiding: true,
  enableFullScreenToggle: true,
  enableTopToolbar: true,
  enableBottomToolbar: true,
    enableColumnActions: true,
    ...(typeof enableFilterMatchHighlighting === 'boolean'
      ? { enableFilterMatchHighlighting }
      : {}),
    muiTablePaperProps: { elevation: 0 },
    // When using server-side pagination, disable all client-side filtering
    // and let the backend handle everything via onGlobalFilterChange
    ...(manualPagination ? { 
      manualFiltering: true, // Backend handles filtering, MRT just displays results
    } : {
      // Use exact-match filter for client-side filtering
      globalFilterFn: exactGlobalFilter,
    }),
  // Allow injecting custom content into the left side of the top toolbar
  renderTopToolbarCustomActions: leftTopToolbar ? (() => leftTopToolbar) : undefined,
    // Style the toolbar alert banner (selected rows)
    muiToolbarAlertBannerProps: {
      variant: 'standard',
      sx: {
        backgroundColor: 'transparent',
        boxShadow: 'none',
        border: 'none',
        color: '#424242 !important',
        p: 0,
        m: 0,
        alignItems: 'center',
        '& .MuiAlert-icon, & .MuiAlert-action': { display: 'none' },
        '& .MuiAlert-message': {
          backgroundColor: 'transparent',
          color: '#424242 !important',
          p: 0,
          m: 0,
        },
        '& .MuiAlert-message *': {
          color: '#424242 !important',
        },
      },
    },
    // Make the table visually match the existing FullTable styles
    muiTableProps: {
      size: 'small',
      stickyHeader: true,
    },
    muiTopToolbarProps: {
      sx: {
        height: 50,
        minHeight: 50,
        py: 0,
        display: 'flex',
        alignItems: 'center',
        // keep both sides vertically centered and reserve space for the global filter to avoid layout shift
        '& .mrt-toolbar-left-actions, & .mrt-toolbar-right-actions': {
          display: 'flex',
          alignItems: 'center',
          height: '100%',
        },
        // Make the selected-count text (left actions) white to match the tools row color
        '& .mrt-toolbar-left-actions *': {
          color: '#eff4f6',
        },
        '& .MuiToolbar-root': {
          display: 'flex',
          alignItems: 'center',
          height: '100%',
        },
        '& .mrt-toolbar-left-actions': {
          flex: 1,
          minWidth: 0,
          color: '#eff4f6',
          backgroundColor: 'transparent',
        },
        // Ensure any chip/paper or box used to show the selected count has no background
        '& .mrt-toolbar-left-actions .MuiChip-root, & .mrt-toolbar-left-actions .MuiPaper-root, & .mrt-toolbar-left-actions .MuiBox-root': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
        // Specifically make the MUI Alert used by MRT (selected rows) transparent and compact
        '& .mrt-toolbar-left-actions .MuiAlert-root': {
          backgroundColor: 'transparent',
          color: '#424242 !important',
          border: 'none',
          boxShadow: 'none',
          padding: 0,
          margin: 0,
          alignItems: 'center',
        },
        '& .mrt-toolbar-left-actions .MuiAlert-message': {
          backgroundColor: 'transparent',
          color: '#424242 !important',
          padding: 0,
          margin: 0,
        },
        '& .mrt-toolbar-left-actions .MuiAlert-message .MuiBox-root': {
          color: '#424242 !important',
        },
        '& .mrt-toolbar-left-actions .MuiAlert-icon, & .mrt-toolbar-left-actions .MuiAlert-action': {
          display: 'none',
        },
        '& .mrt-toolbar-right-actions': {
          width: 'auto',
          flexShrink: 0,
          justifyContent: 'flex-end',
           gap: 0.5, 
          height: '100%',
          alignItems: 'center',
        },

        '& .mrt-toolbar-right-actions .MuiCollapse-root': {
          display: 'inline-flex',
          alignItems: 'center',
          height: '100%',
          minWidth: 0,
        },
        '& .mrt-toolbar-right-actions .MuiCollapse-wrapper': {
          display: 'inline-flex',
          alignItems: 'center',
          height: '100%',
          minWidth: 0,
        },
        '& .mrt-toolbar-right-actions .MuiCollapse-wrapperInner': {
          display: 'inline-flex',
          alignItems: 'center',
          height: '100%',
          minWidth: 0,
        },
        // explicitly center the global filter wrapper provided by MRT
        '& .mrt-global-filter-text-field': {
          alignSelf: 'center',
          display: 'flex',
          alignItems: 'center',
          height: '100%',
           margin: 0,
          padding: 0,
        },
        '& .mrt-global-filter-text-field .MuiFormControl-root': {
          alignSelf: 'center',
          display: 'flex',
          alignItems: 'center',
          margin: '0 !important',
          padding: 0,
          height: 28,
        },
        '& .mrt-toolbar-right-actions .MuiTextField-root': {
          alignSelf: 'center !important',
          display: 'flex',
          alignItems: 'center',
          margin: '0 !important',
          padding: 0,
          height: 28,
        },
        '& .mrt-toolbar-right-actions > *, & .mrt-toolbar-left-actions > *': {
          alignSelf: 'center',
        },
        '& .MuiIconButton-root': {
          height: 36,
          width: 36,
          alignSelf: 'center',
        },
        '& .MuiFormControl-root': {
          alignSelf: 'center',
          verticalAlign: 'middle',
        },
      },
    },
    // Column filters disabled; no need to adjust column action menu
    muiSearchTextFieldProps: {
      variant: 'outlined',
      size: 'small',
      margin: 'none',
      // ensure the TextField itself aligns to the toolbar center (handled in sx)
      sx: {
        minWidth: 280,
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'center',
        my: 0,
        py: 0,
        height: 28,
        transform: 'translateY(3px)',
        '& .MuiOutlinedInput-root': {
          height: 28,
          px: 1,
          py: 0,
          alignItems: 'center',
          display: 'flex',
        },
        // Remove the built-in Search start adornment completely
        '& .MuiInputAdornment-root.MuiInputAdornment-positionStart': {
          display: 'none !important',
        },
        // explicitly hide the SearchIcon SVG 
        '& .MuiInputAdornment-root [data-testid="SearchIcon"], & [data-testid="SearchIcon"]': {
          display: 'none !important',
        },

        '& .MuiInputAdornment-root.MuiInputAdornment-positionStart + .MuiOutlinedInput-input': {
          marginLeft: 0,
        },

        '& .MuiOutlinedInput-root.MuiInputBase-adornedStart .MuiOutlinedInput-input': {
          paddingLeft: '8px',
        },
        '& .MuiOutlinedInput-input': {
         
          lineHeight: '28px',
        },
        '& .MuiInputAdornment-root': {
          alignItems: 'center',
          height: '100%',
        },
        '& .MuiIconButton-root': {
          color: '#eff4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        '& .MuiSvgIcon-root': { color: '#eff4f6' },
        // CLEAR ICON VISIBILITY (end adornment)
        '& .MuiInputAdornment-root .MuiIconButton-root': {
          padding: 0,
          borderRadius: 0,
          backgroundColor: 'transparent',
          width: 'auto',
          height: 'auto',
          minWidth: 0,
          minHeight: 0,
        },
        '& .MuiInputAdornment-root .MuiIconButton-root:hover': {
          backgroundColor: 'transparent',
        },
        '& .MuiInputAdornment-root .MuiIconButton-root .MuiTouchRipple-root': {
          display: 'none',
        },
        '& .MuiInputAdornment-root .MuiSvgIcon-root': {
          color: (theme: any) => (theme?.frankie?.searchBlue ?? '#0B3C5D'),
          fontSize: '1.2rem',
        },
        '& .MuiInputAdornment-root .MuiSvgIcon-root path': {
          fill: (theme: any) => (theme?.frankie?.searchBlue ?? '#0B3C5D'),
          stroke: 'none',
        },
        // Hide the clear icon when there's no text (disabled state)
        '& .MuiInputAdornment-root .MuiIconButton-root.Mui-disabled': {
          display: 'none',
        },
      },
    },
    muiBottomToolbarProps: {
      sx: { minHeight: 40, py: 0.5 },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 'bold',
        backgroundColor: (theme: any) => (theme?.frankie?.searchBlue ?? '#0B3C5D'),
        color: '#eff4f6',
        borderBottom: '2px solid rgb(212, 210, 210)',
        textAlign: 'center',
        height: 'auto',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        overflow: 'visible',
        textOverflow: 'unset',
        lineHeight: 1.2,
        verticalAlign: 'middle',
        position: 'relative',
        // Ensure header content uses flexbox for proper alignment
        '& .Mui-TableHeadCell-Content': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        },
        // Position the column actions (3 dots) at the end
        '& .Mui-TableHeadCell-Content-Actions': {
          position: 'absolute',
          right: 4,
          top: '50%',
          transform: 'translateY(-50%)',
        },
        // internal wrappers and sort labels also allow wrapping
        '& .MuiTableSortLabel-root': { color: 'inherit', whiteSpace: 'normal', alignItems: 'center' },
        '& .MuiTableSortLabel-root.Mui-active': { color: 'inherit', whiteSpace: 'normal' },
        '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
        '& .MuiTableSortLabel-root .MuiTableSortLabel-label': { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' },
        '& .MuiBox-root': { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' },

        '& .MuiIconButton-root': { color: '#eff4f6' },
        '& .MuiSvgIcon-root': { color: '#eff4f6' },
        '& .MuiCheckbox-root': { color: '#eff4f6' },
        '& .MuiCheckbox-root.Mui-checked': { color: '#eff4f6' },
        // block shimmer over header
        '& .MuiSkeleton-root': { display: 'none' },
        // (colors already set above for sort labels)
        '&:hover, &:hover *': {
          color: '#64b5f6',
          cursor: 'pointer',
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: '0.75rem',
        py: '6px',
        px: '8px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '250px',
        borderBottom: '1px solid rgb(212, 210, 210)',
        verticalAlign: 'middle',
        lineHeight: '1.5',
      },
    },
    muiTableContainerProps: {
      sx: {
        overflowX: 'auto',
        overflowY: 'auto',
        // hide linear progress bars so shimmer only shows on rows
        '& .MuiLinearProgress-root': { display: 'none' },
        '&::-webkit-scrollbar': { height: 2 },
        '&::-webkit-scrollbar-track': { background: '#e0e0e0' },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#a0a0a0',
          borderRadius: 3,
        },
        // Sticky last column: header and body need different backgrounds
        '& thead th:last-of-type': {
          position: 'sticky',
          right: 0,
          zIndex: 2,
          textAlign: 'center !important',
          backgroundColor: (theme: any) => (theme?.frankie?.searchBlue ?? '#0B3C5D'),
          color: '#eff4f6',
        },
        '& tbody td:last-of-type': {
          position: 'sticky',
          right: 0,
          zIndex: 2,
          textAlign: 'center !important',
          backgroundColor: '#fff',
        },
        '& th:nth-of-type(2), & td:nth-of-type(2)': {
          minWidth: '200px !important',
          width: '250px !important',
          maxWidth: '250px !important',
        },
        '& th:first-of-type, & td:first-of-type': {
          minWidth: '100px !important',
          paddingLeft: '5px !important',
          textAlign: 'left !important',
        },
        ...(tableContainerSx || {}),
      },
    },
    initialState: { 
      density: 'compact', 
      showGlobalFilter: false,
      // Ensure columns(both visible and hidden) remain in the order passed
      columnOrder: columns.map((col: any) => col.accessorKey || col.id || ''),
    },
    state: { 
      isLoading: showSkeletons && !showSpinner, 
      showProgressBars: false,
      // Ensure columns order remains stable across renders
      columnOrder: columns.map((col: any) => col.accessorKey || col.id || ''),
      ...(loading && { showEmptyRows: false })
    },
    // Loading behavior:
    // - showSpinner: always show custom SMP overlay when loading (appears immediately)
    // - showSkeletons: only show skeleton rows if spinner isn't active and no data exists
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
      },
    },
    // tune skeleton count; MRT will render pageSize or default ~5 rows
    muiSkeletonProps: {
      animation: 'wave',
    },
    // Override MRT localization to control empty message timing
    localization: {
      noRecordsToDisplay: loading ? '' : 'No records found',
    },
    // only after loading is complete
    renderEmptyRowsFallback: () => {
      if (loading) {
        return <div style={{ display: 'none' }} />; 
      }
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 4,
            color: '#666',
            fontStyle: 'italic',
          }}
        >
          No records found
        </Box>
      );
    },
  };

  // Controlled global filter wiring (optional)
  if (typeof globalFilter === 'string') {
    tableProps.state.globalFilter = globalFilter;
    if (globalFilter.trim().length > 0) {
      tableProps.state.showGlobalFilter = true;
    }
  }
  if (onGlobalFilterChange) {
    tableProps.onGlobalFilterChange = onGlobalFilterChange;
  }

  const controllingPagination = manualPagination && pageIndex !== undefined && pageSize !== undefined;
  if (controllingPagination) {
    tableProps.manualPagination = true;
    tableProps.state.pagination = { pageIndex, pageSize };
    if (typeof totalRowCount === 'number') tableProps.rowCount = totalRowCount;
    if (onPaginationChange) tableProps.onPaginationChange = onPaginationChange;
  }
  if (manualSorting) {
    tableProps.manualSorting = true;
    if (onSortingChange) tableProps.onSortingChange = onSortingChange;
  }

  if (Array.isArray(sorting)) {
    tableProps.state.sorting = sorting;
  }
  if (Array.isArray(columnFilters)) {
    tableProps.state.columnFilters = columnFilters;
    if (onColumnFiltersChange) tableProps.onColumnFiltersChange = onColumnFiltersChange;
  }

  if (enableSorting === false) {
    tableProps.enableSorting = false;
  }

  // Row selection wiring (optional, controlled)
  if (enableRowSelection) {
    tableProps.enableRowSelection = true;
    tableProps.enableMultiRowSelection = true;
    if (getRowId) tableProps.getRowId = getRowId;
    if (rowSelection) {
      tableProps.state.rowSelection = rowSelection;
    }
    if (onRowSelectionChange) tableProps.onRowSelectionChange = onRowSelectionChange;
  }

  return (
  <Box sx={{ 
    border: '1px solid #e0e0e0', 
    borderRadius: 1, 
    mt: 0, 
    maxWidth: '100%', 
    overflowX: 'auto',
    // Hide MRT's built-in progress bar since we're using custom overlay
    '& .MuiLinearProgress-root': { display: 'none' },
    position: 'relative',
    minHeight: '200px',
  }}>
      <MRTErrorBoundary>
        {React.createElement(MaterialReactTable as any, tableProps)}
      </MRTErrorBoundary>
      
      {showSpinner && (
        <Box
          sx={{
            position: 'absolute',
            top: '110px',
            left: '1px',
            right: '1px', 
            bottom: '20px', 
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            borderRadius: '4px',
          }}
        >
          {/* Generic circular spinner for app-wide use */}
          <CircularProgress 
            size={60} 
            thickness={4}
            sx={{ 
              color: '#616161',
            }}
          />
        </Box>
      )}
    </Box>
  );
}

export default MRTTable;